/**
 * Smart Universal Search for Filter Products
 * 
 * Searches across:
 * - Title, handle, SKU, barcode
 * - Vendor, product type, tags
 * - compatability.matrix1, matrix2, matrix3
 * - custom.product_description
 * - custom.filter_type
 * - custom.cross_reference
 * - custom.kit_components
 * 
 * Features:
 * - Normalizes special characters for OEM/cross-reference matching
 * - Instant dropdown results
 * - Redirects to /collections/all on form submit
 */

class SmartUniversalSearch extends HTMLElement {
  constructor() {
    super();
    
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('.smart-search-results');
    this.loadingIndicator = this.querySelector('.smart-search-loading');
    
    this.products = [];
    this.searchIndex = [];
    this.dataLoaded = false;
    this.skuToFitmentText = null;
    this.csvRequested = false;
    this.skuToCrossRef = null;
    this.xrefRequested = false;
    this.isOpen = false;
    
    // Configuration
    this.config = {
      minChars: 2,
      maxResults: 12,
      debounceMs: 150
    };
    
    this.init();
  }
  
  init() {
    // One-time highlight styles for Filters-tagged results (accent = theme yellow).
    if (!document.getElementById('uf-filter-highlight-styles')) {
      const st = document.createElement('style');
      st.id = 'uf-filter-highlight-styles';
      st.textContent =
        '.uf-filter-badge{display:inline-block;background:#F7B910;color:#1D2224;font-size:10px;font-weight:700;' +
        'padding:2px 7px;border-radius:3px;text-transform:uppercase;letter-spacing:.4px;line-height:1.4;vertical-align:middle;}' +
        '#smart-search-page-results .ssr-card{position:relative;}' +
        '#smart-search-page-results .ssr-card--filter{box-shadow:inset 0 0 0 2px #F7B910;border-radius:6px;}' +
        '#smart-search-page-results .ssr-card--filter .uf-filter-badge{position:absolute;top:8px;left:8px;z-index:2;}' +
        '.smart-search-result-item.is-filter{background:rgba(247,185,16,.10);}' +
        '.smart-search-result-item.is-filter .result-title{font-weight:600;}';
      document.head.appendChild(st);
    }
    // Load product data
    this.loadProductData();
    
    // Input events
    this.input.addEventListener('input', this.debounce((e) => {
      this.handleSearch(e.target.value);
    }, this.config.debounceMs));
    
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= this.config.minChars && this.resultsContainer.innerHTML) {
        this.open();
      }
    });
    
    this.input.addEventListener('keydown', (e) => this.handleKeyDown(e));
    
    // Close on outside click
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });
  }
  
  /**
   * Load product data. An optional inline blob gives instant first results, then the
   * FULL catalog is pulled from a paginated JSON feed so the search covers EVERY
   * product (not just the first page that Shopify's Liquid loop will render). The
   * assembled catalog is cached per browser session so it only builds once.
   */
  loadProductData() {
    // Instant seed (optional) from the inline blob, if it's still on the page.
    const dataScript = document.getElementById('smart-search-product-data');
    if (dataScript) {
      try {
        const seed = JSON.parse(dataScript.textContent);
        this.products = seed.products || [];
        if (this.products.length) {
          this.buildSearchIndex();
          this.dataLoaded = true;
        }
      } catch (e) { /* ignore seed parse errors; the feed below is authoritative */ }
    }
    this.loadFullCatalog();
  }

  loadFullCatalog() {
    const CACHE_KEY = 'smartSearchCatalog_v15';
    const CACHE_TTL = 15 * 60 * 1000;
    const FEED = '/collections/all?view=searchfeed';

    // Persistent cache helpers: localStorage survives across tabs/visits (so the sidebar rarely waits
    // on a cold feed load); fall back to sessionStorage. MUST match the collection snippet, which
    // shares this key, so both read/write the same store.
    const cacheRead = (key) => {
      try { const v = localStorage.getItem(key); if (v != null) return v; } catch (e) {}
      try { return sessionStorage.getItem(key); } catch (e) { return null; }
    };
    const cacheWrite = (key, val) => {
      try { localStorage.setItem(key, val); return true; } catch (e) {}
      try { sessionStorage.setItem(key, val); return true; } catch (e) {}
      return false;
    };
    const cacheClear = (key) => {
      try { localStorage.removeItem(key); } catch (e) {}
      try { sessionStorage.removeItem(key); } catch (e) {}
    };

    // Reuse the assembled catalog if we already built it (and it isn't stale).
    try {
      const cached = cacheRead(CACHE_KEY);
      const ts = parseInt(cacheRead(CACHE_KEY + '_ts') || '0', 10);
      if (cached && (Date.now() - ts) < CACHE_TTL) {
        const arr = JSON.parse(cached);
        if (arr && arr.length) {
          this.products = arr;
          this.buildSearchIndex();
          this.dataLoaded = true;
          console.log(`Smart Search: ${arr.length} products (cache)`);
          this.loadCsvFitment();
          this.loadCrossRefs();
          return;
        }
      }
    } catch (e) { /* storage unavailable or stale; fall through to fetch */ }

    const wait = ms => new Promise(res => setTimeout(res, ms));
    const getPage = (p, attempt = 0) => fetch(FEED + '&page=' + p)
      .then(r => { if (!r.ok) throw new Error('status ' + r.status); return r.json(); })
      .catch(() => attempt < 3
        ? wait(350 * (attempt + 1)).then(() => getPage(p, attempt + 1))
        : { products: [], _failed: true });

    // Fetch the whole feed ONCE per page and share it with the collection template, so the two
    // scripts don't each fire ~12 page requests at the same time and get throttled to a partial set.
    const loadRaw = () => (async () => {
      const first = await getPage(1);
      let all = (first && first.products) || [];
      const pages = (first && first.pages) || 1;
      const BATCH = 4;
      let anyFailed = false;
      for (let start = 2; start <= pages; start += BATCH) {
        const batch = [];
        for (let p = start; p < start + BATCH && p <= pages; p++) batch.push(getPage(p));
        (await Promise.all(batch)).forEach(d => {
          if (d && d._failed) anyFailed = true;
          if (d && d.products) all = all.concat(d.products);
        });
        if (start + BATCH <= pages) await wait(150);
      }
      all._partial = anyFailed;
      return all;
    })();
    if (!window.__ufFeedCatalog) window.__ufFeedCatalog = loadRaw();

    window.__ufFeedCatalog.then(all => {
      const partial = !!(all && all._partial);
      if (all && all.length) {
        this.products = all;
        this.buildSearchIndex();
        this.dataLoaded = true;
        // Cache a slimmed copy whenever the catalog is substantial (complete, or a near-complete partial).
        // NO matrices here — this shared payload feeds facets/display/text search and must stay small so it
        // parses fast and fits storage. (Kit matrices are large and only the collection page's machine
        // filter needs them; it loads them from a separate key.)
        if (!partial || all.length > 1000) {
          try {
            const slim = all.map(p => {
              const mf = (p.metafields && p.metafields.custom) || {};
              // Compact, pre-joined searchable text for the metafields that the slim otherwise drops
              // (cross references, kit component part numbers, filter type, combo component SKUs). Keeps
              // these findable on warm cache hits without carrying the whole heavy metafield object.
              const extra = [mf.cross_reference, mf.kit_components, mf.filter_type, mf.air_filter_combo_components]
                .filter(Boolean).join(' ');
              return {
                id: p.id, variant_id: p.variant_id,
                title: p.title, handle: p.handle || (p.url || '').split('/products/').pop(),
                url: p.url, image: p.image, image2: p.image2, price: p.price,
                compare_at_price: p.compare_at_price, available: p.available,
                sku: p.sku, skus: p.skus, type: p.type, vendor: p.vendor,
                collections: p.collections, tags: p.tags,
                searchExtra: extra, combo_components: mf.air_filter_combo_components || ''
              };
            });
            if (!cacheWrite(CACHE_KEY, JSON.stringify(slim))) { cacheClear(CACHE_KEY); cacheClear(CACHE_KEY + '_ts'); }
            else cacheWrite(CACHE_KEY + '_ts', String(Date.now()));
          } catch (e) {
            cacheClear(CACHE_KEY); cacheClear(CACHE_KEY + '_ts');
          }
        }
        console.log(`Smart Search: ${all.length} products (feed)${partial ? ' [partial]' : ''}`);
      }
      // CSV enrichments run after the full product set is in place.
      this.loadCsvFitment();
      this.loadCrossRefs();
    }).catch(e => {
      console.warn('Smart Search: feed load failed; using seed only', e);
      this.loadCsvFitment();
      this.loadCrossRefs();
    });
  }
  
  /**
   * Load CSV fitment from the Netlify function and merge it into the search
   * index by SKU. Only products already in the index (real, listed products)
   * pick up CSV machine text, so this is inherently live-SKU filtered.
   */
  loadCsvFitment() {
    if (this.csvRequested) return;
    this.csvRequested = true;
    const FITMENT_URL = 'https://unrivaled-zuccutto-bdbd4b.netlify.app/.netlify/functions/fitment';
    fetch(FITMENT_URL, { headers: { 'Accept': 'application/json' } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data) return;
        this.skuToFitmentText = this.buildSkuFitmentMap(data);
        this.buildSearchIndex(); // rebuild so CSV machine text becomes searchable
        console.log(`Smart Search: merged CSV fitment for ${Object.keys(this.skuToFitmentText).length} SKUs`);
      })
      .catch(e => console.warn('Smart Search: CSV fitment load failed', e));
  }

  /**
   * Load every OEM cross-reference (brand + part #) from the cross-reference CSV
   * and merge it into the search index by SKU, so customers can search OEM numbers.
   */
  loadCrossRefs() {
    if (this.xrefRequested) return;
    this.xrefRequested = true;
    const FITMENT_URL = 'https://unrivaled-zuccutto-bdbd4b.netlify.app/.netlify/functions/fitment';
    fetch(FITMENT_URL + '?xrefall=1', { headers: { 'Accept': 'application/json' } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data || !data.crossRef) return;
        const map = {};
        data.crossRef.forEach(r => {
          const key = this.normalize(r.sku);
          if (!key) return;
          const text = [r.brand, r.part].filter(Boolean).join(' ');
          if (text) (map[key] = map[key] || []).push(text);
        });
        this.skuToCrossRef = map;
        this.buildSearchIndex(); // rebuild so OEM brands / part numbers become searchable
        console.log(`Smart Search: merged cross references for ${Object.keys(this.skuToCrossRef).length} SKUs`);
      })
      .catch(e => console.warn('Smart Search: cross-reference load failed', e));
  }
  
  /**
   * Build a map of normalized SKU -> array of machine-text strings from the
   * function's payload (each machine carries the list of SKUs it fits).
   */
  buildSkuFitmentMap(data) {
    const map = {};
    const add = (sku, text) => {
      const key = this.normalize(sku);
      if (!key || !text) return;
      (map[key] = map[key] || []).push(text);
    };
    (data.heavyDuty || []).forEach(e => {
      const text = [e.make, e.type, e.submodel, e.model, e.variant, e.variant2].filter(Boolean).join(' ');
      (e.skus || []).forEach(sku => add(sku, text));
    });
    (data.automotive || []).forEach(e => {
      const text = [e.year, e.make, e.model, e.trim, e.engine].filter(Boolean).join(' ');
      (e.skus || []).forEach(sku => add(sku, text));
    });
    return map;
  }
  
  /**
   * Collect every SKU a product exposes (single sku, skus[], or variants[]).
   */
  getProductSkus(product) {
    const skus = [];
    if (product.sku) skus.push(product.sku);
    if (Array.isArray(product.skus)) product.skus.forEach(s => { if (s) skus.push(s); });
    if (Array.isArray(product.variants)) product.variants.forEach(v => { if (v && v.sku) skus.push(v.sku); });
    // Air Filter Combo: also expose the two component filter SKUs ("UAF-4949|UAF-2892") so this combo
    // inherits each component's CSV cross references and fitment (they live under the component SKUs).
    const combo = product.combo_components || (product.metafields && product.metafields.custom && product.metafields.custom.air_filter_combo_components) || '';
    if (combo) combo.split('|').forEach(s => { const t = s.trim(); if (t) skus.push(t); });
    return skus;
  }
  
  /**
   * Normalize string for cross-reference matching
   * Removes special characters, lowercases, trims
   */
  normalize(str) {
    if (!str) return '';
    return String(str)
      .toLowerCase()
      .replace(/[-_./\\,;:'"()[\]{}|!@#$%^&*+=<>?~`]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
  
  /**
   * Build search index for all products
   */
  buildSearchIndex() {
    this.searchIndex = this.products.map(product => {
      const searchableText = [];
      
      // === BASIC FIELDS ===
      // Title (high priority)
      if (product.title) {
        searchableText.push(product.title);
      }
      
      // Handle (URL slug)
      if (product.handle) {
        searchableText.push(product.handle.replace(/-/g, ' '));
      }
      
      // SKU (high priority for part number searches)
      if (product.sku) {
        searchableText.push(product.sku);
      }
      
      // Barcode
      if (product.barcode) {
        searchableText.push(product.barcode);
      }
      
      // Vendor
      if (product.vendor) {
        searchableText.push(product.vendor);
      }
      
      // Product type
      if (product.type) {
        searchableText.push(product.type);
      }
      
      // Tags
      if (product.tags && Array.isArray(product.tags)) {
        product.tags.forEach(tag => searchableText.push(tag));
      }
      
      // === METAFIELDS ===
      const mf = product.metafields || {};

      // Compact metafield text preserved in the slim cache (cross references, kit component part numbers,
      // filter type, combo component SKUs) — this is what makes those searchable on warm cache hits.
      if (product.searchExtra) {
        product.searchExtra.split(/[,;\n|]/).forEach(t => {
          const cleaned = t.trim();
          if (cleaned) searchableText.push(cleaned);
        });
      }
      
      // Compatibility matrices (Make|Type|Model|Variant format)
      if (mf.compatability) {
        ['matrix1', 'matrix2', 'matrix3'].forEach(key => {
          const matrix = mf.compatability[key];
          if (matrix) {
            // Split by newlines and pipes
            matrix.split(/[\n|]/).forEach(part => {
              const cleaned = part.trim();
              if (cleaned) searchableText.push(cleaned);
            });
          }
        });
      }
      
      // Custom metafields
      if (mf.custom) {
        // Product description
        if (mf.custom.product_description) {
          searchableText.push(mf.custom.product_description);
        }
        
        // Filter type
        if (mf.custom.filter_type) {
          searchableText.push(mf.custom.filter_type);
        }
        
        // Cross references (likely comma or newline separated)
        if (mf.custom.cross_reference) {
          mf.custom.cross_reference.split(/[,;\n|]/).forEach(ref => {
            const cleaned = ref.trim();
            if (cleaned) searchableText.push(cleaned);
          });
        }
        
        // Kit components
        if (mf.custom.kit_components) {
          mf.custom.kit_components.split(/[,;\n|]/).forEach(comp => {
            const cleaned = comp.trim();
            if (cleaned) searchableText.push(cleaned);
          });
        }
      }
      
      // === CSV FITMENT (merged by SKU) ===
      // Pull in machine applications that live only in the CSV so this product
      // is findable by that machine text too (e.g. "Bobcat E35").
      if (this.skuToFitmentText) {
        this.getProductSkus(product).forEach(sku => {
          const texts = this.skuToFitmentText[this.normalize(sku)];
          if (texts) texts.forEach(t => searchableText.push(t));
        });
      }

      // === OEM CROSS REFERENCES (merged by SKU) ===
      if (this.skuToCrossRef) {
        this.getProductSkus(product).forEach(sku => {
          const texts = this.skuToCrossRef[this.normalize(sku)];
          if (texts) texts.forEach(t => searchableText.push(t));
        });
      }
      
      // Create normalized search string
      const normalizedText = searchableText.map(t => this.normalize(t)).join(' ');
      
      return {
        product: product,
        searchText: normalizedText,
        titleNorm: this.normalize(product.title),
        skuNorm: this.normalize(product.sku),
        isFilter: (product.tags || []).some(t => this.normalize(t) === 'filters')
      };
    });
    this.renderPageResults();
  }

  /**
   * If this page is the search results page (has #smart-search-page-results and a ?q=),
   * render the FULL set of matches from our index (which includes the CSV data that
   * Shopify's native search can't find).
   */
  renderPageResults() {
    const container = document.getElementById('smart-search-page-results');
    if (!container) return; // not the results page
    // On the collection page (grid OR list view), the collection template owns this container — it
    // handles machine + facet + text filtering. The native container is .shop_container.grid in grid
    // view but .shop_container.list in list view, so match either, or the search wrongly takes over
    // the list view and shows "Type a search term".
    if (document.querySelector('.shop_container')) return;
    const query = (new URLSearchParams(window.location.search).get('q') || '').trim();
    const heading = document.getElementById('smart-search-page-heading');

    if (!query) {
      if (heading) heading.textContent = 'Search';
      container.innerHTML = '<p class="ssr-empty">Type a search term to see results.</p>';
      return;
    }
    if (!this.dataLoaded || !this.searchIndex.length) {
      container.innerHTML = '<p class="ssr-empty">Loading products\u2026</p>';
      return;
    }

    const results = this.search(query, 250);
    if (heading) heading.textContent = results.length + ' result' + (results.length !== 1 ? 's' : '') + ' for \u201c' + query + '\u201d';
    if (results.length === 0) {
      container.innerHTML = '<p class="ssr-empty">No products found for \u201c' + this.escapeHtml(query) + '\u201d.</p>';
      return;
    }

    container.innerHTML = results.map(r => {
      const p = r.product;
      const price = p.price ? this.formatMoney(p.price) : '';
      const fil = r.isFilter;
      return '<a class="ssr-card' + (fil ? ' ssr-card--filter' : '') + '" href="' + p.url + '">' +
        (fil ? '<span class="uf-filter-badge">Filter</span>' : '') +
        '<div class="ssr-card-img">' + (p.image ? '<img src="' + p.image + '" alt="" loading="lazy">' : '') + '</div>' +
        '<div class="ssr-card-title">' + this.escapeHtml(p.title) + '</div>' +
        (p.sku ? '<div class="ssr-card-sku">SKU: ' + this.escapeHtml(p.sku) + '</div>' : '') +
        '<div class="ssr-card-price">' + price + '</div>' +
        '</a>';
    }).join('');
  }
  
  /**
   * Handle search input
   */
  handleSearch(query) {
    const searchTerm = query.trim();
    
    if (searchTerm.length < this.config.minChars) {
      this.close();
      return;
    }
    
    if (!this.dataLoaded) {
      this.loadProductData();
      if (!this.dataLoaded) {
        this.renderError('Search data not loaded. Please refresh the page.');
        return;
      }
    }
    
    const results = this.search(searchTerm);
    this.renderResults(results, searchTerm);
    this.open();
  }
  
  /**
   * Perform search with scoring
   */
  search(query, limit) {
    const queryNorm = this.normalize(query);
    const queryWords = queryNorm.split(' ').filter(w => w.length > 1);
    
    const results = [];
    
    this.searchIndex.forEach(entry => {
      let score = 0;
      
      // Exact title match
      if (entry.titleNorm === queryNorm) {
        score += 100;
      }
      // Title starts with query
      else if (entry.titleNorm.startsWith(queryNorm)) {
        score += 80;
      }
      // Title contains query
      else if (entry.titleNorm.includes(queryNorm)) {
        score += 60;
      }
      
      // Exact SKU match (very important for part numbers)
      if (entry.skuNorm && entry.skuNorm === queryNorm) {
        score += 95;
      }
      // SKU contains query
      else if (entry.skuNorm && entry.skuNorm.includes(queryNorm)) {
        score += 70;
      }
      
      // Full query in search text
      if (entry.searchText.includes(queryNorm)) {
        score += 40;
      }
      
      // Individual word matches
      queryWords.forEach(word => {
        if (entry.searchText.includes(word)) {
          score += 15;
        }
      });
      
      if (score > 0) {
        // Prioritize products tagged "Filters": a boost that floats them above comparable-relevance
        // results, while exact title/SKU matches still win. Ties break in favor of Filters.
        if (entry.isFilter) score += 40;
        results.push({ ...entry, score });
      }
    });
    
    // Sort by score descending, then Filters first on ties.
    results.sort((a, b) => (b.score - a.score) || ((b.isFilter ? 1 : 0) - (a.isFilter ? 1 : 0)));
    
    return results.slice(0, limit || this.config.maxResults);
  }
  
  /**
   * Render search results
   */
  renderResults(results, query) {
    if (results.length === 0) {
      this.resultsContainer.innerHTML = `
        <div class="smart-search-no-results">
          <p>No products found for "<strong>${this.escapeHtml(query)}</strong>"</p>
          <p class="suggestions">Try:</p>
          <ul>
            <li>Checking your spelling</li>
            <li>Removing dashes or special characters</li>
            <li>Using fewer keywords</li>
          </ul>
          <a href="/collections/all" class="search-page-link">Browse all products →</a>
        </div>
      `;
      return;
    }
    
    const resultsHtml = results.map((result, index) => {
      const p = result.product;
      const fil = result.isFilter;
      const price = p.price ? this.formatMoney(p.price) : '';
      const comparePrice = p.compare_at_price && p.compare_at_price > p.price 
        ? this.formatMoney(p.compare_at_price) : '';
      
      return `
        <li class="smart-search-result-item${fil ? ' is-filter' : ''}" data-url="${p.url}" tabindex="${index === 0 ? '0' : '-1'}">
          <a href="${p.url}" class="result-link">
            <div class="result-image">
              ${p.image 
                ? `<img src="${p.image}" alt="${this.escapeHtml(p.title)}" loading="lazy">` 
                : '<div class="no-image">No Image</div>'}
            </div>
            <div class="result-content">
              <div class="result-title">${this.highlightMatch(p.title, query)}${fil ? ' <span class="uf-filter-badge">Filter</span>' : ''}</div>
              ${p.sku ? `<div class="result-sku">SKU: ${this.highlightMatch(p.sku, query)}</div>` : ''}
              <div class="result-price">
                ${comparePrice ? `<span class="compare-price">${comparePrice}</span>` : ''}
                <span class="current-price">${price}</span>
              </div>
            </div>
          </a>
        </li>
      `;
    }).join('');
    
    this.resultsContainer.innerHTML = `
      <div class="smart-search-results-header">
        <span class="results-count">${results.length} product${results.length !== 1 ? 's' : ''} found</span>
      </div>
      <ul class="smart-search-results-list" role="listbox">
        ${resultsHtml}
      </ul>
      <div class="smart-search-results-footer">
        <a href="/collections/all?q=${encodeURIComponent(query)}" class="view-all-link">
          View all results for "${this.escapeHtml(query)}" <span class="arrow">→</span>
        </a>
      </div>
    `;
    
    // Add click handlers
    this.resultsContainer.querySelectorAll('.smart-search-result-item').forEach(item => {
      item.addEventListener('click', (e) => {
        if (!e.target.closest('a')) {
          window.location.href = item.dataset.url;
        }
      });
    });
  }
  
  /**
   * Render error message
   */
  renderError(message) {
    this.resultsContainer.innerHTML = `
      <div class="smart-search-error">
        <p>${message}</p>
      </div>
    `;
    this.open();
  }
  
  /**
   * Highlight matching text
   */
  highlightMatch(text, query) {
    if (!text || !query) return this.escapeHtml(text || '');
    
    const escaped = this.escapeHtml(text);
    const queryWords = query.toLowerCase().split(/\s+/).filter(w => w.length > 1);
    
    let result = escaped;
    queryWords.forEach(word => {
      const regex = new RegExp(`(${this.escapeRegex(word)})`, 'gi');
      result = result.replace(regex, '<mark>$1</mark>');
    });
    
    return result;
  }
  
  /**
   * Format money
   */
  formatMoney(cents) {
    if (typeof cents === 'string') cents = parseFloat(cents) * 100;
    const amount = (cents / 100).toFixed(2);
    return `$${amount}`;
  }
  
  /**
   * Escape HTML
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text || '';
    return div.innerHTML;
  }
  
  /**
   * Escape regex special chars
   */
  escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  
  /**
   * Handle keyboard navigation
   */
  handleKeyDown(e) {
    if (!this.isOpen) return;
    
    const items = this.resultsContainer.querySelectorAll('.smart-search-result-item');
    const currentIndex = Array.from(items).findIndex(item => item === document.activeElement);
    
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (currentIndex < items.length - 1) {
          items[currentIndex + 1]?.focus();
        } else if (currentIndex === -1 && items.length > 0) {
          items[0]?.focus();
        }
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        if (currentIndex > 0) {
          items[currentIndex - 1]?.focus();
        } else if (currentIndex === 0) {
          this.input.focus();
        }
        break;
        
      case 'Enter':
        if (document.activeElement.classList.contains('smart-search-result-item')) {
          e.preventDefault();
          window.location.href = document.activeElement.dataset.url;
        } else if (this.input.value.trim()) {
          // Enter from the input box: go to our results page, which lists every match
          // from our index (including CSV cross-references native search can't find).
          e.preventDefault();
          window.location.href = '/collections/all?q=' + encodeURIComponent(this.input.value.trim());
        }
        break;
        
      case 'Escape':
        this.close();
        this.input.blur();
        break;
    }
  }
  
  open() {
    this.isOpen = true;
    this.resultsContainer.style.display = 'block';
    this.setAttribute('open', '');
  }
  
  close() {
    this.isOpen = false;
    this.resultsContainer.style.display = 'none';
    this.removeAttribute('open');
  }
  
  debounce(fn, delay) {
    let timer = null;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }
}

// Register custom element
if (!customElements.get('smart-universal-search')) {
  customElements.define('smart-universal-search', SmartUniversalSearch);
}
